import { Empty } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { cn } from "@fitness-app/utils/src/styles/cn";

import { getVisibleDays, toDayKey, WEEKDAY_LABELS, type CalendarEventsProps } from "./calendarModel";

type AgendaViewProps<T> = Pick<CalendarEventsProps<T>, "eventsByDay" | "getEventKey" | "renderEvent"> & {
  date: Dayjs;
};

const monthShortFormat = new Intl.DateTimeFormat("pl-PL", { month: "short" });

export const AgendaView = <T,>({ date, eventsByDay, getEventKey, renderEvent }: AgendaViewProps<T>) => {
  const today = dayjs();
  const days = getVisibleDays("agenda", date).filter(
    (day) => day.isSame(date, "month") && eventsByDay.has(toDayKey(day)),
  );

  if (!days.length) {
    return (
      <div className="rounded-xl border border-solid border-gray-200 bg-white py-16">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak wydarzeń w tym miesiącu" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-solid border-gray-200 bg-white">
      {days.map((day, index) => {
        const isToday = day.isSame(today, "day");

        return (
          <div
            key={toDayKey(day)}
            className={cn(
              "flex gap-4 px-3 py-3 sm:px-4",
              index > 0 && "border-0 border-t border-solid border-gray-100",
              isToday && "bg-blue-50/40",
            )}
          >
            <div className="flex w-12 shrink-0 flex-col items-center pt-0.5">
              <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                {WEEKDAY_LABELS[day.isoWeekday() - 1]}
              </span>
              <span
                className={cn(
                  "flex size-8 items-center justify-center rounded-full text-base font-semibold tabular-nums",
                  isToday ? "bg-blue-600 text-white" : "text-gray-900",
                )}
              >
                {day.date()}
              </span>
              <span className="text-[11px] text-gray-400">{monthShortFormat.format(day.toDate())}</span>
            </div>
            <div className="flex min-w-0 max-w-xl flex-1 flex-col gap-1">
              {eventsByDay.get(toDayKey(day))?.map((event, eventIndex) => (
                <div key={getEventKey(event, eventIndex)} className="min-w-0">
                  {renderEvent(event, { draggable: false })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
